import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend,
} from "recharts";
import { FileText, ShieldCheck, Users, Eye, AlertTriangle, TrendingUp, Globe2, DollarSign } from "lucide-react";

const COLORS = ["#E62020", "#F59E0B", "#3B82F6", "#10B981", "#8B5CF6", "#EC4899", "#22D3EE"];

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [utm, setUtm] = useState(null);
  const [premium, setPremium] = useState(null);
  const [staffData, setStaffData] = useState({ requests: [], policies: [] });

  useEffect(() => {
    if (user.role === "admin") {
      api.get("/analytics/summary").then((r) => setData(r.data)).catch(() => {});
      api.get("/analytics/utm").then((r) => setUtm(r.data)).catch(() => {});
      api.get("/analytics/monthly-premium").then((r) => setPremium(r.data)).catch(() => {});
    } else {
      Promise.all([api.get("/requests"), api.get("/policies")])
        .then(([r, p]) => setStaffData({ requests: r.data, policies: p.data }))
        .catch(() => {});
    }
  }, [user.role]);

  if (user.role !== "admin") {
    const requests = staffData.requests;
    const policies = staffData.policies;
    const expiring = policies.filter((p) => p.days_left !== null && p.days_left <= 3);
    const cards = [
      { label: "Toplam Talep", value: requests.length, icon: FileText },
      { label: "Yeni Talep", value: requests.filter((r) => r.status === "yeni").length, icon: TrendingUp },
      { label: "Toplam Poliçe", value: policies.length, icon: ShieldCheck },
      { label: "Süresi Yaklaşan", value: expiring.length, icon: AlertTriangle, danger: true },
    ];
    return (
      <div>
        <h1 className="font-heading text-3xl mb-2">Merhaba, {user.name || user.username}</h1>
        <p className="text-zinc-400 mb-8">Personel paneline hoşgeldin. Talepleri ve poliçelerini buradan yönetebilirsin.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((c) => (
            <div key={c.label} className="asg-card rounded-2xl p-6" data-testid={`stat-${c.label}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">{c.label}</div>
                  <div className={`font-heading text-4xl mt-2 ${c.danger ? "asg-primary" : "text-white"}`}>{c.value}</div>
                </div>
                <c.icon className={`w-6 h-6 ${c.danger ? "asg-primary" : "text-zinc-400"}`} />
              </div>
            </div>
          ))}
        </div>
        {expiring.length > 0 && (
          <div className="mt-8 asg-card rounded-2xl p-6 border-l-4" style={{ borderLeftColor: "var(--asg-primary)" }}>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 asg-primary" />
              <h2 className="font-heading text-xl">Süresi Yaklaşan Poliçeler</h2>
            </div>
            <div className="grid gap-2">
              {expiring.map((p) => (
                <div key={p.id} className="flex justify-between border-b border-white/5 py-2 text-sm">
                  <span className="text-white">{p.customer_name} — {p.product_title}</span>
                  <span className={p.days_left < 0 ? "text-red-400" : "text-amber-400"}>
                    {p.days_left < 0 ? `${Math.abs(p.days_left)} gün önce bitti` : `${p.days_left} gün kaldı`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!data) return <div className="text-zinc-400">Yükleniyor...</div>;

  const cards = [
    { label: "Toplam Talep", value: data.total_requests, icon: FileText },
    { label: "Yeni Talep", value: data.new_requests, icon: TrendingUp },
    { label: "Toplam Poliçe", value: data.total_policies, icon: ShieldCheck },
    { label: "Personel", value: data.total_staff, icon: Users },
    { label: "Sayfa Görüntülenme", value: data.total_pageviews, icon: Eye },
    { label: "Süresi Yaklaşan", value: data.expiring_policies.length, icon: AlertTriangle, danger: true },
  ];

  return (
    <div>
      <h1 className="font-heading text-3xl mb-2">Genel Bakış</h1>
      <p className="text-zinc-400 mb-8">Yönetim paneli — tüm site analizleri ve önemli uyarılar.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="asg-card rounded-2xl p-5" data-testid={`stat-${c.label}`}>
            <div className="flex justify-between items-start">
              <div className="text-xs uppercase tracking-widest text-zinc-500">{c.label}</div>
              <c.icon className={`w-4 h-4 ${c.danger ? "asg-primary" : "text-zinc-400"}`} />
            </div>
            <div className={`font-heading text-3xl mt-3 ${c.danger ? "asg-primary" : "text-white"}`}>{c.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <Card className="asg-card border-0 rounded-2xl">
          <CardHeader>
            <CardTitle className="font-heading text-white">Son 7 Gün Talep Yoğunluğu</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.daily_requests}>
                <XAxis dataKey="date" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#1c1c1e", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
                <Bar dataKey="count" fill="#E62020" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="asg-card border-0 rounded-2xl">
          <CardHeader>
            <CardTitle className="font-heading text-white">Ürünlere Göre Talep</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.by_product} dataKey="count" nameKey="product_key" cx="50%" cy="50%" outerRadius={80}>
                  {data.by_product.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend />
                <Tooltip contentStyle={{ background: "#1c1c1e", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {premium && (
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 asg-primary" />
            <h2 className="font-heading text-2xl">Aylık Prim Raporu</h2>
            <span className="ml-auto text-sm text-zinc-400">Son 12 ay toplam: <span className="text-white font-heading text-lg">{Number(premium.total).toLocaleString("tr-TR")} ₺</span></span>
          </div>
          <Card className="asg-card border-0 rounded-2xl">
            <CardContent className="h-72 pt-6" data-testid="monthly-premium-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={premium.months}>
                  <XAxis dataKey="month" stroke="#71717a" fontSize={11} />
                  <YAxis stroke="#71717a" fontSize={11} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip
                    contentStyle={{ background: "#1c1c1e", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }}
                    formatter={(v, k) => k === "total" ? [`${Number(v).toLocaleString("tr-TR")} ₺`, "Prim"] : [v, "Poliçe"]}
                  />
                  <Bar dataKey="total" fill="#E62020" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {utm && (
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <Globe2 className="w-5 h-5 asg-primary" />
            <h2 className="font-heading text-2xl">Reklam Kaynak Raporu</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <UtmCard title="Trafik Kaynağı (utm_source)" rows={utm.by_source} total={utm.total_requests} direct={utm.direct_requests} testid="utm-source" />
            <UtmCard title="Kampanya (utm_campaign)" rows={utm.by_campaign} total={utm.total_requests} testid="utm-campaign" />
            <UtmCard title="Kanal (utm_medium)" rows={utm.by_medium} total={utm.total_requests} testid="utm-medium" />
          </div>
        </div>
      )}

      {data.expiring_policies.length > 0 && (
        <div className="asg-card rounded-2xl p-6 mt-8 border-l-4" style={{ borderLeftColor: "var(--asg-primary)" }}>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 asg-primary" />
            <h2 className="font-heading text-xl">Süresi Yaklaşan Poliçeler (3 gün ve altı)</h2>
          </div>
          <div className="grid gap-2">
            {data.expiring_policies.map((p) => (
              <div key={p.id} className="flex justify-between border-b border-white/5 py-2 text-sm">
                <span className="text-white">
                  <span className="text-zinc-500">[{p.policy_number}]</span> {p.customer_name} — {p.product_title}
                  <span className="text-zinc-500"> · {p.staff_name}</span>
                </span>
                <span className={p.days_left < 0 ? "text-red-400" : "text-amber-400"}>
                  {p.days_left < 0 ? `${Math.abs(p.days_left)} gün önce bitti` : `${p.days_left} gün kaldı`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UtmCard({ title, rows, total, direct, testid }) {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="asg-card rounded-2xl p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-4">{title}</div>
      {rows.length === 0 && direct === undefined && (
        <div className="text-sm text-zinc-500 py-8 text-center">Henüz veri yok</div>
      )}
      {direct !== undefined && (
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1">
            <div className="flex justify-between text-xs mb-1"><span className="text-zinc-300">Direkt / Organik</span><span className="text-zinc-500">{direct}</span></div>
            <div className="h-1.5 rounded-full bg-white/5"><div className="h-1.5 rounded-full bg-zinc-500" style={{ width: `${total ? (direct / total * 100) : 0}%` }} /></div>
          </div>
        </div>
      )}
      <div className="grid gap-3">
        {rows.map((r) => (
          <div key={r.key}>
            <div className="flex justify-between text-xs mb-1"><span className="text-white truncate mr-2">{r.key}</span><span className="text-zinc-400">{r.count}</span></div>
            <div className="h-1.5 rounded-full bg-white/5"><div className="h-1.5 rounded-full" style={{ width: `${(r.count / max) * 100}%`, background: "var(--asg-primary)" }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}
